import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type {
  PlanComingUp,
  PlanItem,
  RequirementSettingGroup,
  RequirementSettingInput,
} from "@/types/plan"

async function fetchPlan(path: string, signal: AbortSignal) {
  const { data } = await apiFetch<{ data: PlanItem[] }>(path, { signal })

  return data
}

export const planQueries = {
  firm: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/plan`),
      queryFn: async ({ signal }) =>
        await fetchPlan(`/firms/${firmId}/plan`, signal),
    }),

  individual: (individualId: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${individualId}/plan`),
      queryFn: async ({ signal }) =>
        await fetchPlan(`/individuals/${individualId}/plan`, signal),
    }),

  settings: () =>
    queryOptions({
      queryKey: apiKey("/settings/requirements"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<{
          data: { groups: RequirementSettingGroup[] }
        }>("/settings/requirements", { signal })

        return data.groups
      },
    }),
}

/** The plan of whichever record a block is about. */
export function recordPlanQuery(subject: {
  type: "firm" | "individual"
  id: string
}) {
  const path =
    subject.type === "firm"
      ? `/firms/${subject.id}/plan`
      : `/individuals/${subject.id}/plan`

  return queryOptions({
    queryKey: apiKey(path),
    queryFn: async ({ signal }) => await fetchPlan(path, signal),
  })
}

/** What the plan of whichever record a block is about is owed now. */
export function recordPlanComingUpQuery(subject: {
  type: "firm" | "individual"
  id: string
}) {
  const path =
    subject.type === "firm"
      ? `/firms/${subject.id}/plan/coming-up`
      : `/individuals/${subject.id}/plan/coming-up`

  return queryOptions({
    queryKey: apiKey(path),
    queryFn: async ({ signal }) => {
      const { data } = await apiFetch<{ data: PlanComingUp }>(path, {
        signal,
      })

      return data
    },
  })
}

/** Saving reassesses the whole organisation in the background. */
export function useUpdateRequirementSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (requirements: RequirementSettingInput[]) => {
      const { data } = await apiFetch<{
        data: { groups: RequirementSettingGroup[] }
      }>("/settings/requirements", { method: "PUT", body: { requirements } })

      return data.groups
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(apiKey("/settings/requirements"), settings)
    },
  })
}
